/**
 * OnboardingPage.tsx — 1804va rev2
 * Clean two-field onboarding: name + role only.
 * Interests inferred by AI from upload behaviour — no manual picking.
 */
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { supabase, ensureRecipient } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import { Button, Input, Spinner } from '@/components/ui'
import { cn } from '@/components/ui/utils'
import type { ParticipantRole } from '@/types'

const ROLES: { value: ParticipantRole; label: string; desc: string; emoji: string }[] = [
  { value: 'creator',    label: 'Creator',    desc: 'Photographer, videographer, artist',   emoji: '📸' },
  { value: 'organiser',  label: 'Organiser',  desc: 'Event team, production, management',   emoji: '🎪' },
  { value: 'sponsor',    label: 'Sponsor',    desc: 'Brand partner, commercial interest',   emoji: '💼' },
  { value: 'publisher',  label: 'Publisher',  desc: 'Media outlet, blog, content platform', emoji: '📰' },
  { value: 'press',      label: 'Press',      desc: 'Journalist, editor, correspondent',    emoji: '✍️' },
]

export default function OnboardingPage({ onComplete }: { onComplete?: () => void }) {
  const navigate = useNavigate()
  const { user, setParticipant } = useAuthStore()
  const [name, setName]         = useState('')
  const [handle, setHandle]     = useState('')
  const [role, setRole]         = useState<ParticipantRole | ''>('')
  const [nameError, setNameError] = useState('')
  const [roleError, setRoleError] = useState('')
  const [done, setDone]         = useState(false)

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('No user')

      const { data: participant, error } = await supabase
        .from('participants')
        .insert({
          name: name.trim(),
          handle: handle.trim() || null,
          role: role || 'creator',
          auth_user_id: user.id,
        })
        .select()
        .single()

      if (error) throw new Error(error.message)

      // Persist participant_id to auth metadata for session recovery
      await supabase.auth.updateUser({
        data: { participant_id: participant.id }
      })

      // Create recipient with no preset interests — AI will populate from behaviour
      await ensureRecipient(participant.id, [], ['photo', 'video', 'audio', 'document'])

      return participant
    },
    onSuccess: (participant) => {
      setParticipant(participant)
      setDone(true)
      setTimeout(() => {
        if (onComplete) onComplete()
        else navigate('/upload', { replace: true })
      }, 1000)
    },
  })

  const handleSubmit = () => {
    let ok = true
    if (!name.trim()) { setNameError('Name is required'); ok = false } else setNameError('')
    if (!role)        { setRoleError('Pick your role');   ok = false } else setRoleError('')
    if (ok) createMutation.mutate()
  }

  if (done) {
    return (
      <div className="min-h-screen bg-room-bg flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="text-6xl">✅</div>
          <h2 className="text-white text-2xl font-bold">You're in The Room</h2>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-room-bg text-white flex flex-col">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-room-accent/8 rounded-full blur-[120px]" />
      </div>

      <div className="relative flex-1 flex flex-col max-w-sm mx-auto w-full px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-black tracking-tight">
            <span className="bg-gradient-to-br from-purple-300 via-room-accent to-purple-500 bg-clip-text text-transparent">
              The Room
            </span>
          </h1>
          <p className="text-neutral-500 text-sm mt-2">Set up your profile to continue</p>
        </div>

        <div className="flex-1 flex flex-col gap-6">
          <div className="space-y-4">
            <Input
              label="Name"
              value={name}
              onChange={e => { setName(e.target.value); setNameError('') }}
              placeholder="Your name"
              error={nameError}
            />
            <Input
              label="Handle (optional)"
              value={handle}
              onChange={e => setHandle(e.target.value.replace(/[^a-z0-9_]/gi, '').toLowerCase())}
              placeholder="@yourhandle"
            />
          </div>

          <div>
            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-3">Your role</p>
            {roleError && <p className="text-red-400 text-xs mb-2">{roleError}</p>}
            <div className="space-y-2">
              {ROLES.map(r => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => { setRole(r.value); setRoleError('') }}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all',
                    role === r.value
                      ? 'border-room-accent bg-room-accent/10'
                      : 'border-neutral-800 hover:border-neutral-600'
                  )}
                >
                  <span className="text-xl">{r.emoji}</span>
                  <div>
                    <p className={cn('text-sm font-semibold', role === r.value ? 'text-white' : 'text-neutral-300')}>{r.label}</p>
                    <p className="text-xs text-neutral-500">{r.desc}</p>
                  </div>
                  {role === r.value && <span className="ml-auto text-room-accent">✓</span>}
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="w-full bg-room-accent hover:bg-fuchsia-600 text-white mt-auto py-3 text-base font-bold"
          >
            {createMutation.isPending ? <Spinner size={14} /> : "Enter The Room →"}
          </Button>

          {createMutation.isError && (
            <p className="text-red-400 text-xs text-center">
              {(createMutation.error as Error).message}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
